import { CircularProgress, Fade, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GridColumns } from '@mui/x-data-grid';
import { Edit } from '@mui/icons-material';
import { AddButton, DataTable, IconTitle, VerticalLine } from '../../../../components';
import { Section, Unit } from '../../../../entities';
import { keyBy } from '../../../../utils/objects';
import NoContent from './components/NoContent';
import UnitsPanel from './components/UnitPanel';
import useStyles from './styles';
import { useFetch, useSections, useUnits } from '../../../../hooks';
import MainContainer from '../../../../components/MainContainer';

interface UpdateButtonProps {
  onClick: () => void;
}

const UpdateButton = ({ onClick }: UpdateButtonProps): JSX.Element => (
  <IconButton onClick={onClick}>
    <Edit />
  </IconButton>
);

export const Units = (): JSX.Element => {
  const { id: condominiumId } = useParams<{ id: string }>();
  const [units, setUnits] = useState<Unit[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionsMapper, setSectionsMapper] = useState<Record<string, Section>>({});
  const [selectedUnit, setSelectedUnit] = useState<Unit>({} as Unit);
  const { enqueueSnackbar } = useSnackbar();
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const sectionsRepository = useSections(condominiumId!);
  const unitsRepository = useUnits(condominiumId!);
  const styles = useStyles();
  const { data: sectionsResponse } = useFetch(sectionsRepository.fetchAll);
  const { data: unitsResponse, loading, refetch } = useFetch(unitsRepository.fetchAll);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    const grouped = keyBy(sectionsResponse ?? [], 'id');
    setSections(sectionsResponse ?? []);
    setSectionsMapper(grouped);
  }, [sectionsResponse]);

  useEffect(() => {
    setUnits(unitsResponse ?? []);
  }, [unitsResponse]);

  useEffect(() => {
    setIsPanelVisible(false);
  }, [condominiumId]);

  const handleCreate = async (value: Unit) => {
    await unitsRepository.create(value);
  };

  const handleImportDocument = async (value: string) => {
    try {
      setIsImporting(true);
      await unitsRepository.importDocument(value);

      enqueueSnackbar('Documento importado com sucesso!', { variant: 'success' });

      await refetch();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Documento invalido ou já importado anteriormente.', { variant: 'error' });
    } finally {
      setIsImporting(false);
    }
  };

  const handleUpdate = async (value: Unit) => {
    await unitsRepository.update(value);
  };

  const handleSetPanelOpen = () => {
    if (selectedUnit) {
      setSelectedUnit({} as Unit);
    }
    setIsPanelVisible(!isPanelVisible);
  };

  const handleSubmit = async (value: Unit) => {
    try {
      if (value.id) {
        await handleUpdate(value);
      } else {
        await handleCreate(value);
      }

      enqueueSnackbar('A unidade foi salva com sucesso!', { variant: 'success' });
      refetch();
      handleSetPanelOpen();
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Não foi possível salvar os dados', { variant: 'error' });
    }
  };

  const showNoContent = useMemo(() => !isPanelVisible && units.length === 0, [isPanelVisible, units]);

  const handleSelectUnit = (unit?: Unit) => {
    if (unit) {
      setSelectedUnit(unit);
    }
    setIsPanelVisible(true);
  };

  if (loading && units.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />{' '}
      </div>
    );
  }

  const headers: GridColumns = [
    {
      field: 'photoUrl',
      headerName: '',
      disableColumnMenu: true,
      editable: false,
      disableExport: true,
      disableReorder: true,
      hideable: false,
      filterable: false,
      sortable: false,
      width: 62,
      align: 'center',
      hideSortIcons: true,
      renderCell: () => <IconTitle title="" />,
    },
    {
      field: 'label',
      headerName: 'Nome',
      editable: false,
      flex: 1,
    },
    {
      field: 'number',
      headerName: 'Número',
      editable: false,
      flex: 1,
    },
    {
      field: 'situation',
      headerName: 'Habilitado',
      editable: false,
      flex: 1,
    },
    {
      field: 'section',
      headerName: 'Bloco',
      editable: false,
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Ações',
      disableColumnMenu: true,
      editable: false,
      disableExport: true,
      disableReorder: true,
      hideable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (value) => <UpdateButton onClick={() => handleSelectUnit(new Unit(value.row))} />,
      hideSortIcons: true,
    },
  ];

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <MainContainer>
            <DataTable
              showAddButton
              onClickAdd={handleSetPanelOpen}
              onChangeImport={handleImportDocument}
              columns={headers}
              rows={
                units.map((unit) => ({
                  ...unit,
                  number: parseInt(unit.number, 10),
                  situation: unit.disabled ? 'Não' : 'Sim',
                  section: sectionsMapper[unit.sectionId!] ? sectionsMapper[unit.sectionId!].label : 'Sem bloco',
                })) || []
              }
              loading={loading || isImporting}
              showOnChangeImport
            />
          </MainContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <UnitsPanel
        visible={isPanelVisible}
        unit={selectedUnit}
        sections={sections}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
      />
      {!isPanelVisible && units.length === 0 && (
        <>
          <AddButton label="adicionar unidade" onClick={() => handleSetPanelOpen()} />
        </>
      )}
    </div>
  );
};

export default Units;
